import React, { useEffect, useState } from 'react'
import {
  Drawer,
  Switch,
  InputNumber,
  Input,
  Button,
  message,
  Popconfirm
} from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import './Levels.scss'
import { api } from '../../../../../../../../services'
import { useTranslation } from 'react-i18next'

const defaultPointsState = {
  levels_enabled: false,
  level_rules: [
    { level_name: 'Level 1', is_enabled: false, min_point: 0, max_point: 100 }
  ]
}

const Levels = ({ visible, data, refreshData, onClose }) => {
  const [state, setState] = useState(defaultPointsState)
  const [editingLevel, setEditingLevel] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [newLevel, setNewLevel] = useState({
    level_name: '',
    is_enabled: true,
    min_point: 0,
    max_point: 100
  })
  const { t } = useTranslation()

  useEffect(() => {
    if (data) {
      setState(data)
    }
  }, [data])

  const handleLevelsEnabledChange = checked => {
    setState(prev => ({ ...prev, levels_enabled: checked }))
  }

  const addLevel = () => {
    if (!newLevel.level_name.trim()) {
      message.error('Please enter a level name')
      return
    }

    if (newLevel.min_point > newLevel.max_point) {
      message.error('Minimum points cannot exceed maximum points')
      return
    }

    setState(prev => ({
      ...prev,
      level_rules: [...(prev.level_rules || []), { ...newLevel }]
    }))

    setNewLevel({
      level_name: '',
      is_enabled: true,
      min_point: 0,
      max_point: 100
    })
  }

  const deleteLevel = index => {
    const newLevelRules = state.level_rules.filter((_, i) => i !== index)
    setState(prev => ({ ...prev, level_rules: newLevelRules }))
  }

  const startEditLevel = index => {
    if (state.level_rules[index]) {
      setEditingLevel({
        index,
        data: { ...state.level_rules[index] }
      })
    }
  }

  const saveEditLevel = () => {
    if (!editingLevel?.data?.level_name?.trim()) {
      message.error('Please enter a level name')
      return
    }

    if (editingLevel.data.min_point > editingLevel.data.max_point) {
      message.error('Minimum points cannot exceed maximum points')
      return
    }

    const newLevelRules = [...state.level_rules]
    newLevelRules[editingLevel.index] = editingLevel.data
    setState(prev => ({ ...prev, level_rules: newLevelRules }))

    setEditingLevel(null)
  }

  const cancelEditLevel = () => {
    setEditingLevel(null)
  }

  // Fixed function to handle level name change
  const handleEditLevelNameChange = value => {
    setEditingLevel(prev => {
      if (!prev || !prev.data) return prev
      return {
        index: prev.index,
        data: {
          ...prev.data,
          level_name: value
        }
      }
    })
  }

  // Fixed function to handle enabled change
  const handleEditLevelEnabledChange = checked => {
    setEditingLevel(prev => {
      if (!prev || !prev.data) return prev
      return {
        index: prev.index,
        data: {
          ...prev.data,
          is_enabled: checked
        }
      }
    })
  }

  // Fixed function to handle min point change
  const handleEditMinPointChange = value => {
    setEditingLevel(prev => {
      if (!prev || !prev.data) return prev
      return {
        index: prev.index,
        data: {
          ...prev.data,
          min_point: value || 0
        }
      }
    })
  }

  // Fixed function to handle max point change
  const handleEditMaxPointChange = value => {
    setEditingLevel(prev => {
      if (!prev || !prev.data) return prev
      return {
        index: prev.index,
        data: {
          ...prev.data,
          max_point: value || 0
        }
      }
    })
  }

  const updateGlobalSettings = async () => {
    let data = {
      levels_enabled: state.levels_enabled,
      level_rules: state.level_rules
    }
    setIsLoading(true)
    try {
      await api.leaderboard.updateGlobalSettings(data)
      onClose()
      refreshData()
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }
  const handleDiscard = () => {
    if (onClose) {
      onClose()
    }
  }
  return (
    <Drawer
      title={t('v5:levels_configuration')}
      visible={visible}
      onClose={onClose}
      width={500}
      className="levels-drawer"
      placement="right"
    >
      <div className="levels-content">
        {/* Levels Enabled Toggle */}
        <div className="levels-section">
          <div className="toggle-row">
            <Switch
              checked={state.levels_enabled}
              onChange={handleLevelsEnabledChange}
            />
            <span className="toggle-label">{t('v4:levels_enabled')}</span>
          </div>
        </div>

        {/* Existing Levels */}
        <div className="levels-section">
          <h3 className="section-title">{t('v4:level_rules')}</h3>

          {state.level_rules &&
            state.level_rules.map((level, index) => (
              <div key={index} className="level-rule-card">
                {editingLevel && editingLevel.index === index ? (
                  // Edit Mode
                  <div className="level-edit-form">
                    <div className="level-edit-row">
                      <Input
                        value={editingLevel.data?.level_name || ''}
                        onChange={e => {
                          if (e && e.target) {
                            handleEditLevelNameChange(e.target.value)
                          }
                        }}
                        placeholder={t('v5:level_name')}
                        style={{ flex: 1 }}
                      />
                      <Switch
                        checked={editingLevel.data?.is_enabled || false}
                        onChange={handleEditLevelEnabledChange}
                        size="small"
                      />
                    </div>
                    <div className="level-edit-row">
                      <InputNumber
                        value={editingLevel.data?.min_point || 0}
                        onChange={handleEditMinPointChange}
                        min={0}
                        placeholder="Min"
                        style={{ flex: 1 }}
                      />
                      <span>-</span>
                      <InputNumber
                        value={editingLevel.data?.max_point || 0}
                        onChange={handleEditMaxPointChange}
                        min={0}
                        placeholder="Max"
                        style={{ flex: 1 }}
                      />
                      <span>{t('v4:points')}</span>
                    </div>
                    <div className="level-edit-actions">
                      <Button
                        shape="round"
                        size="small"
                        onClick={cancelEditLevel}
                      >
                        {t('buttons:cancel')}
                      </Button>
                      <Button
                        type="primary"
                        shape="round"
                        size="small"
                        onClick={saveEditLevel}
                      >
                        {t('buttons:save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="level-rule-content">
                    <div className="level-rule-header">
                      <div className="level-rule-info">
                        <h4>{level.level_name}</h4>
                        <span className="level-range">
                          {level.min_point} - {level.max_point} {t('v4:points')}
                        </span>
                        <span
                          className={`level-status ${
                            level.is_enabled ? 'enabled' : 'disabled'
                          }`}
                        >
                          {level.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="level-rule-actions">
                        <Button
                          type="text"
                          onClick={() => startEditLevel(index)}
                          size="small"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                        >
                          <EditOutlined />
                        </Button>
                        <Popconfirm
                          title="Delete this level?"
                          onConfirm={() => deleteLevel(index)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="text"
                            danger
                            size="small"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            <DeleteOutlined style={{ color: 'red' }} />
                          </Button>
                        </Popconfirm>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

          {/* Add New Level Form */}
          <div className="add-level-section">
            <h4>{t('v4:add_new_level')}</h4>
            <div className="level-edit-form">
              <div className="level-edit-row">
                <Input
                  value={newLevel.level_name}
                  onChange={e => {
                    const value = e.target.value
                    setNewLevel(prev => ({
                      ...prev,
                      level_name: value
                    }))
                  }}
                  placeholder="Level name"
                  style={{ flex: 1 }}
                />
                <Switch
                  checked={newLevel.is_enabled}
                  onChange={checked =>
                    setNewLevel(prev => ({
                      ...prev,
                      is_enabled: checked
                    }))
                  }
                  size="small"
                />
              </div>
              <div className="level-edit-row">
                <InputNumber
                  value={newLevel.min_point}
                  onChange={value =>
                    setNewLevel(prev => ({
                      ...prev,
                      min_point: value || 0
                    }))
                  }
                  min={0}
                  placeholder="Min"
                  style={{ flex: 1 }}
                />
                <span>-</span>
                <InputNumber
                  value={newLevel.max_point}
                  onChange={value =>
                    setNewLevel(prev => ({
                      ...prev,
                      max_point: value || 0
                    }))
                  }
                  min={0}
                  placeholder="Max"
                  style={{ flex: 1 }}
                />
                <span>{t('v4:points')}</span>
              </div>
              <Button
                type="primary"
                shape="round"
                onClick={addLevel}
                disabled={!newLevel.level_name.trim()}
                size="small"
              >
                <PlusOutlined />
                {t('v4:add_level')}
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="modal-footer fixed-footer">
          <Button
            onClick={handleDiscard}
            className="shl-secondary-btn"
            disabled={isLoading}
          >
            {t('v4:discard')}
          </Button>
          <Button
            onClick={updateGlobalSettings}
            className="shl-primary-btn"
            loading={isLoading}
          >
            {t('buttons:save')}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default Levels
